/**
 * Cadence + Last-8-weeks strip + triage flags for trainer Members roster.
 *
 * Calendar weeks: Monday-start (ISO-style), local timezone via YYYY-MM-DD keys.
 * Plan schedule for a week ≈ sessions with assigned_by = viewing trainer scheduled that week
 * (other trainers’ / member self-assigned / null-assigner rows are excluded).
 *
 * Join / tenure for cadence window (prefer in order):
 * 1. relationship.startedAt when set
 * 2. else relationship.createdAt (invite accepted / roster join proxy)
 */

import {
  addCalendarDaysToYmd,
  calendarDayKeyFromScheduledDate,
  localCalendarDateKey,
  sessionCountsAsCompletedWorkout,
  sessionIsFromTrainer,
  type SessionLike,
} from './memberLast7DaysActivity';

export type StripBarClass = 'miss' | 's1' | 's2' | 's3' | 's4';

export const STRIP_WEEK_LABELS = [
  '8 weeks ago',
  '7 weeks ago',
  '6 weeks ago',
  '5 weeks ago',
  '4 weeks ago',
  '3 weeks ago',
  '2 weeks ago',
  'This week',
] as const;

export const STRIP_BAR_MEANING: Record<StripBarClass, string> = {
  s1: '~25% of personal weekly target',
  s2: '~50% of personal weekly target',
  s3: '~75% of personal weekly target',
  s4: '100%+ of personal weekly target',
  miss: '0 sessions — target missed',
};

export const CADENCE_TOOLTIP =
  'Average completed sessions per week, calculated over the last 8 weeks (or since they joined, if more recent).';

export const NEEDS_ATTENTION_DAYS = 10;
export const STALE_INVITE_DAYS = 14;

export const STALE_INVITE_TOOLTIP =
  'Invite is still pending more than 14 days after it was sent. Consider resending.';

const YMD_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function parseYmdAsLocal(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function calendarDaysBetween(earlierYmd: string, laterYmd: string): number {
  const a = parseYmdAsLocal(earlierYmd);
  const b = parseYmdAsLocal(laterYmd);
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

/** Monday YYYY-MM-DD of the local calendar week containing `ymd`. */
export function startOfWeekMonday(ymd: string): string {
  const d = parseYmdAsLocal(ymd);
  const day = d.getDay(); // 0 Sun … 6 Sat
  const diffToMon = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMon);
  return localCalendarDateKey(d);
}

export function addCalendarWeeksToYmd(ymd: string, deltaWeeks: number): string {
  return addCalendarDaysToYmd(ymd, deltaWeeks * 7);
}

/**
 * Last 8 Monday-start weeks ending with the week that contains `todayYmd`.
 * Index 0 = oldest (8 weeks ago), index 7 = this week.
 */
export function getLast8WeekStarts(todayYmd: string): string[] {
  const thisWeek = startOfWeekMonday(todayYmd);
  const starts: string[] = [];
  for (let i = 7; i >= 0; i--) {
    starts.push(addCalendarWeeksToYmd(thisWeek, -i));
  }
  return starts;
}

export function isoToLocalYmd(isoOrYmd: string | undefined | null): string | null {
  if (!isoOrYmd?.trim()) return null;
  const s = isoOrYmd.trim();
  if (YMD_ONLY.test(s)) return s;
  const t = Date.parse(s);
  if (Number.isNaN(t)) return s.length >= 10 ? s.slice(0, 10) : null;
  return localCalendarDateKey(new Date(t));
}

/**
 * Roster join day for cadence window.
 * Prefer startedAt (relationship start); else createdAt.
 */
export function joinDateYmd(relationship: {
  startedAt?: string;
  createdAt?: string;
}): string | null {
  return isoToLocalYmd(relationship.startedAt) ?? isoToLocalYmd(relationship.createdAt);
}

export function stripBarClass(completed: number, planned: number): StripBarClass {
  if (planned === 0 || completed === 0) return 'miss';
  const ratio = completed / planned;
  if (ratio < 0.375) return 's1';
  if (ratio < 0.625) return 's2';
  if (ratio < 0.875) return 's3';
  return 's4';
}

export type WeekCounts = { weekStart: string; completed: number; planned: number; bar: StripBarClass };

function trainerSessions(sessions: SessionLike[], trainerId: string): SessionLike[] {
  return sessions.filter((s) => sessionIsFromTrainer(s, trainerId));
}

function dayKey(s: SessionLike): string {
  return calendarDayKeyFromScheduledDate(s.scheduledDate);
}

/** Sessions whose schedule day falls in [weekStart, weekStart+6]. */
export function sessionsInWeek(sessions: SessionLike[], weekStart: string): SessionLike[] {
  const weekEnd = addCalendarDaysToYmd(weekStart, 6);
  return sessions.filter((s) => {
    const dk = dayKey(s);
    return dk >= weekStart && dk <= weekEnd;
  });
}

/**
 * Planned = trainer-scoped sessions scheduled that calendar week (plan calendar proxy).
 * Completed = completed among those / all completed with day in week (count completed sessions that week).
 */
export function countWeekPlannedAndCompleted(
  sessions: SessionLike[],
  trainerId: string,
  weekStart: string
): { planned: number; completed: number } {
  const inWeek = sessionsInWeek(trainerSessions(sessions, trainerId), weekStart);
  const planned = inWeek.length;
  const completed = inWeek.filter((s) => sessionCountsAsCompletedWorkout(s)).length;
  return { planned, completed };
}

export function computeEightWeekStrip(
  sessions: SessionLike[],
  trainerId: string,
  options?: { todayYmd?: string }
): WeekCounts[] {
  const todayYmd = options?.todayYmd ?? localCalendarDateKey(new Date());
  const weekStarts = getLast8WeekStarts(todayYmd);
  return weekStarts.map((weekStart) => {
    const { planned, completed } = countWeekPlannedAndCompleted(sessions, trainerId, weekStart);
    return { weekStart, planned, completed, bar: stripBarClass(completed, planned) };
  });
}

/**
 * Number of Monday-start calendar weeks from max(join, first of last-8) through this week inclusive.
 * Minimum 1 to avoid ÷0.
 */
export function cadenceWeekCount(joinYmd: string | null, todayYmd: string): number {
  const weekStarts = getLast8WeekStarts(todayYmd);
  const windowEarliest = weekStarts[0];
  const tenureStart = joinYmd && joinYmd > windowEarliest ? joinYmd : windowEarliest;
  const firstWeek = startOfWeekMonday(tenureStart);
  const thisWeek = weekStarts[7];
  if (firstWeek > thisWeek) return 1;
  const days = calendarDaysBetween(firstWeek, thisWeek);
  return Math.max(1, Math.floor(days / 7) + 1);
}

export function completedSessionsInCadenceWindow(
  sessions: SessionLike[],
  trainerId: string,
  joinYmd: string | null,
  todayYmd: string
): number {
  const weekStarts = getLast8WeekStarts(todayYmd);
  const windowEarliest = weekStarts[0];
  const fromYmd = joinYmd && joinYmd > windowEarliest ? joinYmd : windowEarliest;
  return trainerSessions(sessions, trainerId).filter((s) => {
    if (!sessionCountsAsCompletedWorkout(s)) return false;
    const dk = dayKey(s);
    return dk >= fromYmd && dk <= todayYmd;
  }).length;
}

export function computeCadence(
  sessions: SessionLike[],
  trainerId: string,
  joinYmd: string | null,
  options?: { todayYmd?: string }
): { value: number; weekCount: number; completedInWindow: number } {
  const todayYmd = options?.todayYmd ?? localCalendarDateKey(new Date());
  const weekCount = cadenceWeekCount(joinYmd, todayYmd);
  const completedInWindow = completedSessionsInCadenceWindow(sessions, trainerId, joinYmd, todayYmd);
  return {
    value: completedInWindow / weekCount,
    weekCount,
    completedInWindow,
  };
}

export function formatCadenceDisplay(value: number): string {
  return value.toFixed(1);
}

/**
 * Roster Last session: only rows with status === 'completed'.
 * Do not treat completedAt alone as done — scheduled rows sometimes carry stray timestamps.
 */
export function sessionIsRosterCompleted(s: SessionLike): boolean {
  return String(s.status).toLowerCase() === 'completed';
}

/**
 * Roster Next session: incomplete upcoming only (scheduled or in progress).
 */
export function sessionIsRosterUpcoming(s: SessionLike): boolean {
  if (sessionIsRosterCompleted(s)) return false;
  const st = String(s.status).toLowerCase();
  return st === 'scheduled' || st === 'in_progress';
}

export type LastSessionDisplay = {
  label: string;
  warn: boolean;
  daysAgo: number | null;
};

export function computeLastCompletedDay(
  sessions: SessionLike[],
  trainerId: string,
  options?: { todayYmd?: string }
): string | null {
  const todayYmd = options?.todayYmd ?? localCalendarDateKey(new Date());
  let best: string | null = null;
  for (const s of trainerSessions(sessions, trainerId)) {
    if (!sessionIsRosterCompleted(s)) continue;
    const dk = dayKey(s);
    if (!dk) continue;
    // Never treat a future-dated row as a completed "last session"
    if (dk > todayYmd) continue;
    if (best == null || dk > best) best = dk;
  }
  return best;
}

export function formatLastSessionDisplay(
  lastCompletedYmd: string | null,
  todayYmd: string
): LastSessionDisplay {
  if (!lastCompletedYmd) {
    return { label: '—', warn: true, daysAgo: null };
  }
  const daysAgo = calendarDaysBetween(lastCompletedYmd, todayYmd);
  if (daysAgo <= 0) return { label: 'Today', warn: false, daysAgo: 0 };
  if (daysAgo === 1) return { label: 'Yesterday', warn: false, daysAgo: 1 };
  const warn = daysAgo >= NEEDS_ATTENTION_DAYS;
  return { label: `${daysAgo} days ago`, warn, daysAgo };
}

export type NextSessionDisplay = {
  label: string;
  kind: 'today' | 'tomorrow' | 'date' | 'empty';
  nextYmd: string | null;
};

export function formatNextSessionDay(nextYmd: string, todayYmd: string): NextSessionDisplay {
  const diff = calendarDaysBetween(todayYmd, nextYmd);
  if (diff <= 0) return { label: 'Today', kind: 'today', nextYmd };
  if (diff === 1) return { label: 'Tomorrow', kind: 'tomorrow', nextYmd };
  const d = parseYmdAsLocal(nextYmd);
  const label = d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  return { label, kind: 'date', nextYmd };
}

/** Next upcoming scheduled/in_progress workout on/after today (not completed). */
export function computeNextScheduledDay(
  sessions: SessionLike[],
  trainerId: string,
  todayYmd: string
): string | null {
  const upcoming = trainerSessions(sessions, trainerId)
    .filter((s) => sessionIsRosterUpcoming(s) && dayKey(s) >= todayYmd)
    .map((s) => dayKey(s))
    .filter(Boolean)
    .sort();
  return upcoming[0] ?? null;
}

export function formatNextSessionDisplay(
  nextYmd: string | null,
  todayYmd: string
): NextSessionDisplay {
  if (!nextYmd) return { label: 'Not scheduled', kind: 'empty', nextYmd: null };
  return formatNextSessionDay(nextYmd, todayYmd);
}

export function hasCompletedOnDay(
  sessions: SessionLike[],
  trainerId: string,
  dayYmd: string
): boolean {
  return trainerSessions(sessions, trainerId).some(
    (s) => sessionIsRosterCompleted(s) && dayKey(s) === dayYmd
  );
}

export function hasScheduledOnDay(
  sessions: SessionLike[],
  trainerId: string,
  dayYmd: string
): boolean {
  return trainerSessions(sessions, trainerId).some(
    (s) => sessionIsRosterUpcoming(s) && dayKey(s) === dayYmd
  );
}

export function isTrainingToday(
  sessions: SessionLike[],
  trainerId: string,
  todayYmd: string
): boolean {
  return hasCompletedOnDay(sessions, trainerId, todayYmd) || hasScheduledOnDay(sessions, trainerId, todayYmd);
}

export function isNeedsAttention(
  sessions: SessionLike[],
  trainerId: string,
  todayYmd: string
): boolean {
  return getNeedsAttentionReasons(sessions, trainerId, todayYmd).length > 0;
}

export type NeedsAttentionReason = 'no_completed' | 'inactive' | 'no_upcoming';

/** Why a member is flagged Needs attention (may be more than one). */
export function getNeedsAttentionReasons(
  sessions: SessionLike[],
  trainerId: string,
  todayYmd: string
): NeedsAttentionReason[] {
  const reasons: NeedsAttentionReason[] = [];
  const last = computeLastCompletedDay(sessions, trainerId, { todayYmd });
  if (last == null) {
    reasons.push('no_completed');
  } else if (calendarDaysBetween(last, todayYmd) >= NEEDS_ATTENTION_DAYS) {
    reasons.push('inactive');
  }
  if (computeNextScheduledDay(sessions, trainerId, todayYmd) == null) {
    reasons.push('no_upcoming');
  }
  return reasons;
}

export function formatNeedsAttentionTooltip(reasons: NeedsAttentionReason[]): string | null {
  if (reasons.length === 0) return null;
  const parts: string[] = [];
  for (const reason of reasons) {
    if (reason === 'no_completed') {
      parts.push('No completed sessions yet.');
    } else if (reason === 'inactive') {
      parts.push(`No completed session in ${NEEDS_ATTENTION_DAYS}+ days.`);
    } else if (reason === 'no_upcoming') {
      parts.push('No upcoming session scheduled.');
    }
  }
  return parts.join(' ');
}

export function isInviteStale(invitedAtIso: string, todayYmd: string): boolean {
  const invitedYmd = isoToLocalYmd(invitedAtIso);
  if (!invitedYmd) return false;
  return calendarDaysBetween(invitedYmd, todayYmd) > STALE_INVITE_DAYS;
}

export function inviteAgeDays(invitedAtIso: string, todayYmd: string): number | null {
  const invitedYmd = isoToLocalYmd(invitedAtIso);
  if (!invitedYmd) return null;
  return calendarDaysBetween(invitedYmd, todayYmd);
}

export function formatInviteAbsoluteDate(invitedAtIso: string): string {
  const ymd = isoToLocalYmd(invitedAtIso);
  if (!ymd) return '—';
  const d = parseYmdAsLocal(ymd);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatInviteAgeLabel(days: number | null): string {
  if (days == null) return '—';
  if (days <= 0) return 'Just now';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export function pad2(n: number): string {
  return String(Math.max(0, n)).padStart(2, '0');
}

export function initialsFromNameOrEmail(name: string, email: string): string {
  const fromName = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
  if (fromName.length >= 1) return fromName.slice(0, 2);
  const handle = email.split('@')[0]?.replace(/[^a-zA-Z]/g, ' ').trim() || '';
  const parts = handle.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (handle.slice(0, 2) || '??').toUpperCase();
}

export type MemberRosterMetrics = {
  strip: WeekCounts[];
  cadence: number;
  last: LastSessionDisplay;
  next: NextSessionDisplay;
  trainingToday: boolean;
  needsAttention: boolean;
  needsAttentionTooltip: string | null;
};

export function computeMemberRosterMetrics(
  sessions: SessionLike[],
  trainerId: string,
  joinYmd: string | null,
  options?: { todayYmd?: string }
): MemberRosterMetrics {
  const todayYmd = options?.todayYmd ?? localCalendarDateKey(new Date());
  const strip = computeEightWeekStrip(sessions, trainerId, { todayYmd });
  const { value: cadence } = computeCadence(sessions, trainerId, joinYmd, { todayYmd });
  const lastDay = computeLastCompletedDay(sessions, trainerId, { todayYmd });
  const last = formatLastSessionDisplay(lastDay, todayYmd);
  const nextDay = computeNextScheduledDay(sessions, trainerId, todayYmd);
  const next = formatNextSessionDisplay(nextDay, todayYmd);
  const attentionReasons = getNeedsAttentionReasons(sessions, trainerId, todayYmd);
  return {
    strip,
    cadence,
    last,
    next,
    trainingToday: isTrainingToday(sessions, trainerId, todayYmd),
    needsAttention: attentionReasons.length > 0,
    needsAttentionTooltip: formatNeedsAttentionTooltip(attentionReasons),
  };
}
